import type { ReactNode } from "react";
import { BRAND, TERMS } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Rule } from "../ui/primitives.js";

/**
 * THE TERMS PAGE (2026-10-22, Andre's decisions for the release that ships
 * hard verification and password reset). The privacy page's composition: the
 * card on the bloom, the whole width below the `sm` breakpoint so a phone gets
 * no sideways scroll, reachable cold, outside the shell. The sign-in card's
 * notice line links here, and so does the privacy page's first section; this
 * page links back to privacy.
 *
 * Every word rendered here comes from TERMS in design/copy.ts verbatim; the
 * truthfulness rules live on that block. This file is only layout. "back."
 * goes to the route the page was reached from (the app decides: sign in when
 * it was reached cold).
 */
export function Terms({ onBack }: { onBack: () => void }): ReactNode {
  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div
        data-testid="terms-card"
        className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12"
      >
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />

        <h1 className="mt-6 mb-0 font-display text-display font-bold">{TERMS.title}</h1>
        <div className="mt-2 font-sans text-micro">{TERMS.updated}</div>

        <p className="mt-8 mb-0 font-sans text-body">{TERMS.intro}</p>
        <Rule className="mt-6" />

        {TERMS.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="mt-8 mb-0 font-display text-nav font-bold">{section.heading}</h2>
            <p className="mt-2 mb-0 font-sans text-body">{section.body}</p>
            {"list" in section ? (
              <ul className="mt-2 mb-0 list-disc pl-6 font-sans text-body">
                {section.list.map((item) => (
                  <li key={item} className="mt-1">
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
            {"more" in section
              ? section.more.map((paragraph) => (
                  <p key={paragraph} className="mt-4 mb-0 font-sans text-body">
                    {paragraph}
                  </p>
                ))
              : null}
          </section>
        ))}

        <Rule className="mt-10" />
        <div className="mt-6 flex flex-wrap items-baseline gap-6">
          <button
            type="button"
            onClick={onBack}
            className="w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-nav text-ink hover:bg-marker"
          >
            {TERMS.back}
          </button>
          <a href="#/privacy" className="font-sans text-nav text-link underline">
            {TERMS.privacy}
          </a>
        </div>
      </div>
    </div>
  );
}
